import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "@/ui/layouts";
import { CetDateLabel, MonthCheckboxGroup, type TSearchableOption } from "@/ui/molecules";
import {
    HomepageBoard,
    HomepageFilters,
    HomepageGroupSection,
    type THomepageTypeFilter,
} from "@/ui/organisms";
import type {
    TAsyncState,
    THomepageBoard,
    THomepageColumnKey,
    THomepageRow,
    THomepageView,
} from "@/store";
import { routerPaths } from "@/modules/lasalle/router";

/**
 * View parameters (injected dependencies)
 */
export type TViewHomepageParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useBoard: () => TAsyncState<THomepageBoard>;
        useSubmissionFilter: () => TSearchableOption | null;
        onSearchSubmissions: (query: string) => Promise<TSearchableOption[]>;
        onSubmissionChange: (option: TSearchableOption | null) => void;
        useBusinessPartnerFilter: () => TSearchableOption | null;
        onSearchBusinessPartners: (query: string) => Promise<TSearchableOption[]>;
        onBusinessPartnerChange: (option: TSearchableOption | null) => void;
        /** True for the admin tier (`homepage_view_all`), whose board is
         * unscoped and therefore the only one the Type / finance-team filters
         * have anything to narrow (§6). */
        useCanFilterAll: () => boolean;
        /** Whether the caller can open the TB/GL files list the "All
         * submissions" link points to (`tbgl_files_page_access`). */
        useCanViewAllSubmissions: () => boolean;
        useTypeFilter: () => THomepageTypeFilter;
        onTypeFilterChange: (value: THomepageTypeFilter) => void;
        useFinanceUserFilter: () => TSearchableOption | null;
        onSearchFinanceUsers: (query: string) => Promise<TSearchableOption[]>;
        onFinanceUserChange: (option: TSearchableOption | null) => void;
        useSelectedPeriods: () => string[];
        /** Reveal more rows in a capped column, and whether that is in flight. */
        onShowMore: () => void;
        useIsLoadingMore: () => boolean;
        useBoardView: () => THomepageView;
        onBoardViewChange: (view: THomepageView) => void;
        onPeriodsChange: (periods: string[]) => void;
        onRowClick: (row: THomepageRow, columnKey: THomepageColumnKey) => void;
    };
};

/**
 * View props (from router/parent)
 */
export type TViewHomepageProps = Record<string, never>;

/**
 * Creates the Homepage v2 view component.
 *
 * Layout only: filter row (dropdowns, "All submissions" link and the month
 * period selector), 3-column board, column counts and CET date. Which columns render — Service Provider or Accountant
 * — is decided entirely server-side (the board response carries exactly one
 * view's columns), and which filters render follows from that plus the caller's
 * `homepage_view_all` permission, so this view holds no role rules of its own.
 */
export const createViewHomepage = ({ useCases }: TViewHomepageParams) => {
    const ViewHomepage: React.FC<TViewHomepageProps> = () => {
        const {
            onMount,
            onUnmount,
            useBoard,
            useSubmissionFilter,
            onSearchSubmissions,
            onSubmissionChange,
            useBusinessPartnerFilter,
            onSearchBusinessPartners,
            onBusinessPartnerChange,
            useCanFilterAll,
            useCanViewAllSubmissions,
            useTypeFilter,
            onTypeFilterChange,
            useFinanceUserFilter,
            onSearchFinanceUsers,
            onFinanceUserChange,
            useSelectedPeriods,
            onShowMore,
            useIsLoadingMore,
            useBoardView,
            onBoardViewChange,
            onPeriodsChange,
            onRowClick,
        } = useCases();
        const boardState = useBoard();
        const board = boardState.data;
        const submission = useSubmissionFilter();
        const businessPartner = useBusinessPartnerFilter();
        const canFilterAll = useCanFilterAll();
        const canViewAllSubmissions = useCanViewAllSubmissions();
        const typeFilter = useTypeFilter();
        const financeUser = useFinanceUserFilter();
        const selectedPeriods = useSelectedPeriods();
        const isLoadingMore = useIsLoadingMore();
        const boardView = useBoardView();
        // The Business Partner filter belongs to the Accountant and Admin
        // boards (§7.3). `board.view` is the server's decision about which
        // column set the caller gets, so keying off it keeps the role logic
        // server-side; the admin tier gets it regardless of the column set it
        // is currently rendering.
        const showBusinessPartnerFilter = canFilterAll || board?.view === "accountant";

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        return (
            <PageLayout title="Home" actions={<CetDateLabel date={board?.serverDate} />}>
                <HomepageFilters
                    className="mb-4"
                    submission={submission}
                    onSearchSubmissions={onSearchSubmissions}
                    onSubmissionChange={onSubmissionChange}
                    businessPartner={businessPartner}
                    onSearchBusinessPartners={
                        showBusinessPartnerFilter ? onSearchBusinessPartners : undefined
                    }
                    onBusinessPartnerChange={
                        showBusinessPartnerFilter ? onBusinessPartnerChange : undefined
                    }
                    typeFilter={typeFilter}
                    onTypeFilterChange={canFilterAll ? onTypeFilterChange : undefined}
                    financeUser={financeUser}
                    onSearchFinanceUsers={canFilterAll ? onSearchFinanceUsers : undefined}
                    onFinanceUserChange={canFilterAll ? onFinanceUserChange : undefined}
                    boardView={boardView}
                    onBoardViewChange={canFilterAll ? onBoardViewChange : undefined}
                    periodSelector={
                        // Hidden when no month is on offer (every in-scope deal
                        // starts after the window) rather than showing a bare label.
                        board && board.availablePeriods.length > 0 ? (
                            <MonthCheckboxGroup
                                availablePeriods={board.availablePeriods}
                                selectedPeriods={selectedPeriods}
                                onChange={onPeriodsChange}
                            />
                        ) : undefined
                    }
                    trailing={
                        canViewAllSubmissions ? (
                            <Link
                                to={routerPaths.files.tbgl}
                                data-testid="homepage-all-submissions-link"
                                className="shrink-0 whitespace-nowrap text-sm font-medium text-primary underline underline-offset-4 hover:text-primary/80"
                            >
                                All submissions
                            </Link>
                        ) : undefined
                    }
                />
                {boardState.status === "pending" && !board && (
                    <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
                )}
                {boardState.status === "rejected" && !board && (
                    <div className="py-12 text-center text-sm text-destructive">
                        Couldn&apos;t load the homepage. Please try again.
                    </div>
                )}
                {board && (
                    <HomepageGroupSection label="Submission">
                        <HomepageBoard
                            board={board}
                            onRowClick={onRowClick}
                            onShowMore={onShowMore}
                            isLoadingMore={isLoadingMore}
                        />
                    </HomepageGroupSection>
                )}
            </PageLayout>
        );
    };

    return ViewHomepage;
};
